import React from 'react';
import { Check } from 'lucide-react';

/**
 * ProcessStepper — horizontal step-by-step flow.
 * Props:
 *   steps      – array of { label, subtitle, icon }
 *   activeStep – 0-based index of current step (for tracking state; -1 = not applied)
 *   completedSteps – array of booleans per step
 *   theme      – theme object from SERVICE_THEMES
 *   dates      – optional array of date strings per step
 */
export default function ProcessStepper({ steps, activeStep = -1, completedSteps, theme, dates }) {
  return (
    <div className="w-full">
      {/* Desktop stepper */}
      <div className="hidden md:flex items-start justify-between relative">
        {/* Connecting line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0" style={{ left: '10%', right: '10%' }} />
        {steps.map((step, idx) => {
          const isCompleted = completedSteps?.[idx] || false;
          const isActive = idx === activeStep;
          const StepIcon = step.icon;

          return (
            <div key={idx} className="flex flex-col items-center relative z-10 flex-1">
              {/* Node */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                  isCompleted
                    ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                    : isActive
                    ? `${theme.stepColor} text-white shadow-md`
                    : 'bg-gray-100 text-gray-400'
                }`}
                style={isActive ? { backgroundColor: theme.accent } : undefined}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <StepIcon className="w-4 h-4" />
                )}
              </div>
              {/* Label */}
              <p
                className={`mt-2.5 text-xs font-semibold text-center max-w-[110px] leading-tight ${
                  isCompleted
                    ? 'text-emerald-700'
                    : isActive
                    ? 'text-gray-900'
                    : 'text-gray-400'
                }`}
              >
                {step.label}
              </p>
              {/* Subtitle / date */}
              <p className="mt-1 text-[11px] text-gray-400 text-center">
                {dates?.[idx] || step.subtitle}
              </p>
            </div>
          );
        })}
      </div>

      {/* Mobile stepper — vertical */}
      <div className="flex md:hidden flex-col gap-0">
        {steps.map((step, idx) => {
          const isCompleted = completedSteps?.[idx] || false;
          const isActive = idx === activeStep;
          const isLast = idx === steps.length - 1;
          const StepIcon = step.icon;

          return (
            <div key={idx} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                    isCompleted
                      ? 'bg-emerald-500 text-white'
                      : isActive
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                  style={isActive ? { backgroundColor: theme.accent } : undefined}
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-3.5 h-3.5" />}
                </div>
                {!isLast && (
                  <div className={`w-0.5 h-8 ${isCompleted ? 'bg-emerald-300' : 'bg-gray-200'}`} />
                )}
              </div>
              <div className="pb-6">
                <p className={`text-sm font-semibold ${isCompleted ? 'text-emerald-700' : isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                  {step.label}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{dates?.[idx] || step.subtitle}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
